<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import type { DonorFinancialYear } from '@/types/donorFinancialYear'
import { formatMoneyFromPence } from '@/helpers/moneyDisplay'
import { useGivingRecord } from '@/composables/useGivingRecord'
import { useManualGivingDonations } from '@/composables/useManualGivingDonations'
import { useAddDonationModal } from '@/composables/useAddDonationModal'
import { useRecurringDonations } from '@/composables/useRecurringDonations'
import { recurringDonationsEnabled } from '@/helpers/recurringDonationsEnabled'
import DashboardSegmentedTabs from '@/components/dashboard/ui/DashboardSegmentedTabs.vue'
import GivingRecordList from '@/components/dashboard/giving/GivingRecordList.vue'
import GivingRecordMetricsSkeleton from '@/components/dashboard/giving/GivingRecordMetricsSkeleton.vue'
import RecurringSeriesList from '@/components/dashboard/giving/RecurringSeriesList.vue'

const props = withDefaults(
  defineProps<{
    sortedYears: DonorFinancialYear[]
    isLoading?: boolean
  }>(),
  {
    isLoading: false,
  }
)

const { manualDonations, isLoading: isManualLoading } =
  useManualGivingDonations()
const { openAddDonationModal } = useAddDonationModal()
const recurringEnabled = recurringDonationsEnabled()
const { recurringSeries } = useRecurringDonations()
const pageView = ref<'history' | 'recurring'>('history')

const { t } = useI18n()

const showRecurringTab = computed(
  () => recurringEnabled && recurringSeries.value.length > 0
)

watch(showRecurringTab, (show) => {
  if (!show) pageView.value = 'history'
})

const pageTabs = computed(() => [
  { id: 'history', label: t('Dashboard.Giving.Views.History') },
  { id: 'recurring', label: t('Dashboard.Giving.Views.Recurring') },
])

const {
  yearFilter,
  periodFilterOptions,
  activeTab,
  tabRows,
  metricsPeriodLabel,
  metrics,
  setTab,
} = useGivingRecord(toRef(props, 'sortedYears'), manualDonations)

const isContentLoading = computed(
  () => props.isLoading || isManualLoading.value
)

const segmentedTabs = computed(() => [
  { id: 'all', label: t('Dashboard.Giving.Tabs.All') },
  { id: 'swiftaid', label: t('Dashboard.Giving.Tabs.Swiftaid') },
  { id: 'manual', label: t('Dashboard.Giving.Tabs.Manual') },
])

const metricsSubKeys = computed(() => {
  const period = metricsPeriodLabel.value
  if (period === 'all') {
    return {
      giving: 'Dashboard.Giving.Metrics.TotalGivingSubAll',
      giftAid: 'Dashboard.Giving.Metrics.GiftAidSub',
      charities: 'Dashboard.Giving.Metrics.CharitiesSubAll',
    }
  }
  if (period === 'current') {
    return {
      giving: 'Dashboard.Giving.Metrics.TotalGivingSubYear',
      giftAid: 'Dashboard.Giving.Metrics.GiftAidSubYear',
      charities: 'Dashboard.Giving.Metrics.CharitiesSubYear',
    }
  }
  return {
    giving: 'Dashboard.Giving.Metrics.TotalGivingSubSelected',
    giftAid: 'Dashboard.Giving.Metrics.GiftAidSubSelected',
    charities: 'Dashboard.Giving.Metrics.CharitiesSubSelected',
    year: period,
  }
})

const onTabChange = (tab: string) => {
  setTab(tab as 'all' | 'swiftaid' | 'manual')
}
</script>

<template>
  <div class="sa-giving-paid">
    <DashboardSegmentedTabs
      v-if="showRecurringTab"
      v-model="pageView"
      class="sa-giving-paid__views"
      :tabs="pageTabs"
      :aria-label="t('Dashboard.Giving.Views.AriaLabel')"
    />

    <div class="sa-giving-paid__toolbar">
      <template v-if="pageView === 'history'">
        <label for="giving-year-filter" class="visually-hidden">
          {{ $t('Dashboard.Giving.FilterLabel') }}
        </label>
        <select
          id="giving-year-filter"
          v-model="yearFilter"
          class="sa-year-select"
          :disabled="isContentLoading"
        >
          <option value="all">{{ $t('Dashboard.Giving.FilterAll') }}</option>
          <option
            v-for="opt in periodFilterOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </option>
        </select>
      </template>
      <button
        type="button"
        class="sa-btn-primary"
        @click="
          openAddDonationModal(
            pageView === 'history' && yearFilter !== 'all' ? yearFilter : undefined
          )
        "
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.Giving.AddDonation') }}
      </button>
    </div>

    <template v-if="pageView === 'history'">
    <GivingRecordMetricsSkeleton v-if="isContentLoading" />

    <div
      v-else
      class="sa-g3 sa-giving-paid__metrics"
      :class="{
        'sa-giving-paid__metrics--with-pair': metrics.showGiftAidedGiving,
      }"
    >
      <!--
        When some gifts have no Gift Aid, show that total as a second labelled
        figure in this card. A fourth column would squeeze the row and sit
        beside Gift Aid, which is a different number.
      -->
      <div
        class="sa-metric-card"
        :class="{ 'sa-metric-card--pair': metrics.showGiftAidedGiving }"
        :role="metrics.showGiftAidedGiving ? 'group' : undefined"
        :aria-label="
          metrics.showGiftAidedGiving
            ? $t('Dashboard.Giving.Metrics.GivingPairAria')
            : undefined
        "
      >
        <div class="sa-metric-card__pane">
          <div class="sa-metric-label">
            {{ $t('Dashboard.Giving.Metrics.TotalGiving') }}
          </div>
          <div class="sa-metric-value sa-metric-value--md">
            {{ formatMoneyFromPence(metrics.totalGivingPence) }}
          </div>
        </div>
        <div
          v-if="metrics.showGiftAidedGiving"
          class="sa-metric-card__rule"
          aria-hidden="true"
        />
        <div v-if="metrics.showGiftAidedGiving" class="sa-metric-card__pane">
          <div class="sa-metric-label">
            {{ $t('Dashboard.Giving.Metrics.GiftAidedGiving') }}
          </div>
          <div class="sa-metric-value sa-metric-value--md">
            {{ formatMoneyFromPence(metrics.giftAidedGivingPence) }}
          </div>
        </div>
        <div class="sa-metric-sub">
          {{
            metricsSubKeys.year
              ? $t(metricsSubKeys.giving, { year: metricsSubKeys.year })
              : $t(metricsSubKeys.giving)
          }}
        </div>
      </div>
      <div class="sa-metric-card">
        <div class="sa-metric-label">
          {{ $t('Dashboard.Giving.Metrics.GiftAid') }}
        </div>
        <div class="sa-metric-value sa-metric-value--md">
          {{ formatMoneyFromPence(metrics.totalGiftAidPence) }}
        </div>
        <div class="sa-metric-sub">
          {{
            metricsSubKeys.year
              ? $t(metricsSubKeys.giftAid, { year: metricsSubKeys.year })
              : $t(metricsSubKeys.giftAid)
          }}
        </div>
      </div>
      <div class="sa-metric-card">
        <div class="sa-metric-label">
          {{ $t('Dashboard.Giving.Metrics.Charities') }}
        </div>
        <div class="sa-metric-value sa-metric-value--md">
          {{ metrics.charitiesCount }}
        </div>
        <div class="sa-metric-sub">
          {{
            metricsSubKeys.year
              ? $t(metricsSubKeys.charities, { year: metricsSubKeys.year })
              : $t(metricsSubKeys.charities)
          }}
        </div>
      </div>
    </div>

    <!--
      One filter, two controls. Tabs have room on desktop; at phone widths the
      third tab clips off the strip and scrolling tabs do not read as a filter,
      so a labelled select takes over there. CSS swaps them at 767.98px so the
      hidden one leaves the accessibility tree.
    -->
    <DashboardSegmentedTabs
      v-model="activeTab"
      class="sa-giving-paid__tabs"
      :tabs="segmentedTabs"
      :aria-label="$t('Dashboard.Giving.Tabs.AriaLabel')"
      @update:model-value="onTabChange"
    />
    <div class="sa-list-filter">
      <label for="giving-source-filter" class="sa-list-filter__label">
        {{ $t('Dashboard.Giving.Tabs.FilterLabel') }}
      </label>
      <select
        id="giving-source-filter"
        class="sa-year-select"
        :value="activeTab"
        :disabled="isContentLoading"
        @change="onTabChange(($event.target as HTMLSelectElement).value)"
      >
        <option v-for="tab in segmentedTabs" :key="tab.id" :value="tab.id">
          {{ tab.label }}
        </option>
      </select>
    </div>

    <GivingRecordList
      class="sa-giving-paid__donations"
      :rows="tabRows"
      :variant="activeTab"
      :loading="isContentLoading"
    />

    <div v-if="activeTab === 'manual'" class="sa-giving-paid__add-another">
      <button
        type="button"
        class="sa-btn-primary"
        @click="
          openAddDonationModal(yearFilter === 'all' ? undefined : yearFilter)
        "
      >
        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
            clip-rule="evenodd"
          />
        </svg>
        {{ $t('Dashboard.Giving.AddAnother') }}
      </button>
    </div>
    </template>

    <RecurringSeriesList
      v-else-if="recurringEnabled"
      :series="recurringSeries"
    />
  </div>
</template>
